/**
 * Global API Error Handler
 * Handles common API errors including token verification failures
 */

import { useAuth } from '../context/AuthContext';

interface ApiErrorResponse {
  error?: string;
  errorMessage?: string;
  message?: string;
}

export const handleApiError = (error: any): void => {
  // Try to parse error response
  let errorData: ApiErrorResponse = {};
  
  if (error?.response?.data) {
    errorData = error.response.data;
  } else if (error?.data) {
    errorData = error.data;
  } else if (typeof error === 'object' && error !== null) {
    errorData = error;
  }

  // Check for token verification errors
  const isTokenError = 
    errorData.error === 'verification_failed' ||
    errorData.errorMessage === 'Invalid token' ||
    errorData.message === 'Invalid token' ||
    errorData.error?.includes('invalid_token') ||
    errorData.error?.includes('unauthorized');

  if (isTokenError) {
    // Clear auth state and redirect to signin
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    sessionStorage.removeItem('userData');
    
    // Redirect to signin page
    window.location.href = '/signin';
    return;
  }

  // Log other errors for debugging
  console.error('API Error:', error);
};

// Enhanced fetch wrapper with error handling
export const apiFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    
    // Check for token errors in response
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json().catch(() => ({}));
        handleApiError({ response: { status: response.status, data: errorData } });
      }
    }
    
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// Hook for using API error handler in components
export const useApiErrorHandler = () => {
  const { logout } = useAuth();
  
  const handleError = (error: any) => {
    let errorData: ApiErrorResponse = {};
    
    if (error?.response?.data) {
      errorData = error.response.data;
    } else if (error?.data) {
      errorData = error.data;
    } else if (typeof error === 'object' && error !== null) {
      errorData = error;
    }

    const isTokenError = 
      errorData.error === 'verification_failed' ||
      errorData.errorMessage === 'Invalid token' ||
      errorData.message === 'Invalid token' ||
      errorData.error?.includes('invalid_token') ||
      errorData.error?.includes('unauthorized');

    if (isTokenError) {
      logout();
      window.location.href = '/signin';
      return;
    }

    console.error('API Error:', error);
  };

  return { handleError };
};

